/**
 * api.js
 * Centralized API service for Constellation KG
 * Maps to backend services and routes
 * 
 * DIAGNOSTIC VERSION - Enhanced logging to debug logout loop
 */

import axios from 'axios';

// =========================================
// Base Configuration
// =========================================

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CACHE_PREFIX = 'knowledge:';
const CACHE_DURATION = 300; // 5 minutes

// =========================================
// Logout State Management & Diagnostics
// =========================================
let isLoggingOut = false;
const requestLog = [];
const MAX_LOG_ENTRIES = 50;

// Helper to add entries to the log with circular buffer behavior
const addToLog = (entry) => {
  requestLog.push({
    ...entry,
    timestamp: new Date().toISOString(),
    timeMs: Date.now()
  });
  if (requestLog.length > MAX_LOG_ENTRIES) {
    requestLog.shift();
  }
};

// Helper to dump the full log
const dumpRequestLog = () => {
  console.group('📊 REQUEST LOG DUMP');
  requestLog.forEach((entry, index) => {
    console.log(`[${index}]`, entry);
  });
  console.groupEnd();
};

/**
 * Axios instance with default configuration
 */
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Required for cookie handling
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  timeout: 300000 // 300 second timeout
});

// =========================================
// Request/Response Interceptors
// =========================================
axiosInstance.interceptors.request.use(
  (config) => {
    const logEntry = {
      type: 'REQUEST',
      method: config.method?.toUpperCase(),
      url: config.url,
      isLoggingOut: isLoggingOut,
      hasData: !!config.data
    };
    
    addToLog(logEntry);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 API Request:', logEntry);
    }
    
    return config;
  },
  (error) => {
    addToLog({
      type: 'REQUEST_ERROR',
      error: error.message,
      isLoggingOut: isLoggingOut
    });
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    const logEntry = {
      type: 'RESPONSE_SUCCESS',
      url: response.config.url,
      status: response.status,
      isLoggingOut: isLoggingOut
    };
    
    addToLog(logEntry);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 API Response:', {
        url: response.config.url,
        status: response.status,
        dataSize: response.data ? JSON.stringify(response.data).length : 0
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    
    const logEntry = {
      type: 'RESPONSE_ERROR',
      url: originalRequest?.url,
      status: status,
      isLoggingOut: isLoggingOut,
      hadRetryFlag: originalRequest?._retry,
      isAuthEndpoint: originalRequest?.url?.includes('/auth/')
    };
    
    addToLog(logEntry);
    
    console.error('❌ API Error:', logEntry);
    
    // CRITICAL CHECK #1: If we're logging out, reject immediately without any retry logic
    if (isLoggingOut) {
      console.warn('🚫 Request failed during logout - rejecting without retry');
      addToLog({
        type: 'BLOCKED_BY_LOGOUT_FLAG',
        url: originalRequest?.url,
        status: status
      });
      return Promise.reject(error);
    }
    
    // CRITICAL CHECK #2: Don't intercept auth endpoints
    if (originalRequest?.url?.includes('/auth/')) {
      console.log('⚠️ Auth endpoint failed - not intercepting');
      return Promise.reject(error);
    }
    
    // Try to refresh token on 401 for non-auth endpoints
    if (status === 401 && !originalRequest._retry) {
      console.log('🔄 Attempting token refresh for failed request');
      originalRequest._retry = true;
      
      addToLog({
        type: 'REFRESH_ATTEMPT_START',
        originalUrl: originalRequest?.url,
        isLoggingOut: isLoggingOut
      });
      
      // Triple-check we didn't start logging out while we were deciding to retry
      if (isLoggingOut) {
        console.warn('🚫 Logout started during retry decision - aborting');
        return Promise.reject(error);
      }
      
      try {
        console.log('🔑 Calling authService.refresh()...');
        const refreshResponse = await authService.refresh();
        
        addToLog({
          type: 'REFRESH_SUCCESS',
          hasUser: !!refreshResponse.user
        });
        
        if (refreshResponse.user) {
          console.log('✅ Token refresh successful, retrying original request');
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error('💥 Token refresh failed:', refreshError);
        
        addToLog({
          type: 'REFRESH_FAILED',
          error: refreshError.message,
          status: refreshError.response?.status
        });
        
        // Set the logout flag before redirecting to prevent more refresh attempts
        console.log('🔒 Setting isLoggingOut = true due to refresh failure');
        isLoggingOut = true;
        
        await cacheHelpers.clearAll();
        
        console.log('🔀 Redirecting to /login');
        dumpRequestLog();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// =========================================
// Cache Helpers
// =========================================

export const cacheHelpers = {
  get: async (key) => {
    try {
      const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`);
      if (!cached) return null;
      
      const { data, expiresAt } = JSON.parse(cached);
      if (Date.now() > expiresAt) {
        localStorage.removeItem(`${CACHE_PREFIX}${key}`);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  },

  set: async (key, data) => {
    try {
      localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify({
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + (CACHE_DURATION * 1000)
      }));
    } catch (error) {
      console.error('Cache set error:', error);
    }
  },

  invalidate: async (patterns) => {
    try {
      const keys = Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_PREFIX))
        .filter(key => patterns.some(pattern => key.includes(pattern)));
      
      keys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Cache invalidation error:', error);
    }
  },

  clearAll: async () => {
    try {
      const keys = Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_PREFIX));
      
      keys.forEach(key => localStorage.removeItem(key));
      console.log('🧹 Cache cleared:', keys.length, 'items');
    } catch (error) {
      console.error('Cache clear error:', error);
    }
  }
};

// =========================================
// Authentication Service
// =========================================

export const authService = {
  login: async (credentials) => {
    try {
      console.log('🔐 Login attempt starting');
      // Reset the logout flag when logging in
      isLoggingOut = false;
      
      addToLog({
        type: 'LOGIN_START',
        isLoggingOut: isLoggingOut
      });
      
      const response = await axiosInstance.post('/api/auth/login', credentials);
      
      addToLog({
        type: 'LOGIN_SUCCESS',
        hasUser: !!response.data.user
      });
      
      console.log('✅ Login successful');
      return response.data;
    } catch (error) {
      console.error('❌ Login error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      
      addToLog({
        type: 'LOGIN_FAILED',
        status: error.response?.status,
        message: error.message
      });
      
      throw error;
    }
  },

  logout: async () => {
    console.group('🚪 LOGOUT PROCESS STARTING');
    console.log('Current isLoggingOut value:', isLoggingOut);
    console.log('Setting isLoggingOut = true');
    
    // CRITICAL: Set this flag BEFORE any logout actions
    isLoggingOut = true;
    
    console.log('isLoggingOut is now:', isLoggingOut);
    console.log('Request log entries:', requestLog.length);
    
    addToLog({
      type: 'LOGOUT_START',
      isLoggingOut: isLoggingOut
    });
    
    try {
      console.log('📤 Calling POST /api/auth/logout');
      await axiosInstance.post('/api/auth/logout');
      
      addToLog({
        type: 'LOGOUT_API_SUCCESS'
      });
      
      console.log('✅ Logout API call successful');
      await cacheHelpers.clearAll();
    } catch (error) {
      console.error('⚠️ Logout API call failed (continuing anyway):', error);
      
      addToLog({
        type: 'LOGOUT_API_FAILED',
        error: error.message,
        status: error.response?.status
      });
      
      await cacheHelpers.clearAll();
    }
    
    console.log('📊 Dumping request log before redirect:');
    dumpRequestLog();
    console.groupEnd();
    
    // Note: Don't reset isLoggingOut here - let the page reload/redirect handle cleanup
  },

  refresh: async () => {
    console.log('🔄 Token refresh requested');
    
    addToLog({
      type: 'REFRESH_API_CALL',
      isLoggingOut: isLoggingOut
    });
    
    const response = await axiosInstance.post('/api/auth/refresh');
    
    console.log('✅ Token refresh API successful');
    return response.data;
  },

  checkStatus: async () => {
    console.log('🔍 Checking auth status');
    
    addToLog({
      type: 'AUTH_CHECK',
      isLoggingOut: isLoggingOut
    });
    
    const response = await axiosInstance.get('/api/auth/check');
    return response.data;
  },
  
  updateProfile: async (userData) => {
    try {
      const response = await axiosInstance.put('/api/auth/update-profile', userData);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  /**
   * Record affirmative acceptance of the Terms of Service and Privacy Policy.
   * Sends no body — the server records at its own current versions
   * (ckg3-backend/config/documents.js) and ignores anything the client sends.
   * Returns { message, user } so the caller can refresh auth context.
   */
  acceptDocuments: async () => {
    try {
      const response = await axiosInstance.post('/api/auth/accept-documents');
      return response.data;
    } catch (error) {
      console.error('Accept documents error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  changePassword: async (passwordData) => {
    try {
      const response = await axiosInstance.post('/api/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Change password error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  forgotPassword: async (email) => {
    try {
      console.log('🔑 Forgot password request');
      const response = await axiosInstance.post('/api/auth/forgot-password', { email });
      console.log('✅ Forgot password request successful');
      return response.data;
    } catch (error) {
      console.error('❌ Forgot password error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  verifyMagicLink: async (token) => {
    try {
      console.log('🔗 Verifying magic link token');
      // Reset the logout flag when logging in via magic link
      isLoggingOut = false;

      const response = await axiosInstance.get(`/api/auth/verify-magic-link/${token}`);
      console.log('✅ Magic link verification successful');
      return response.data;
    } catch (error) {
      console.error('❌ Magic link verification error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  register: async (userData) => {
    try {
      console.log('📝 Registration attempt');
      const response = await axiosInstance.post('/api/auth/register', userData);
      console.log('✅ Registration successful');
      return response.data;
    } catch (error) {
      console.error('❌ Registration error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  resendVerification: async (email) => {
    try {
      const response = await axiosInstance.post('/api/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('❌ Resend verification error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  },

  verifyEmail: async (token) => {
    try {
      console.log('📧 Verifying email token');
      const response = await axiosInstance.get(`/api/auth/verify-email/${token}`);
      console.log('✅ Email verification successful');
      return response.data;
    } catch (error) {
      console.error('❌ Email verification error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  }
};

// =========================================
// Waitlist Service
// =========================================
export const waitlistService = {
  join: async (formData) => {
    try {
      const response = await axiosInstance.post('/api/waitlist/join', formData);
      return response.data;
    } catch (error) {
      console.error('Waitlist join error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      throw error;
    }
  }
};

/**
 * ADDITIONS TO api.js
 * Add this export service to your existing api.js file
 * 
 * Location: Add this after the existing services (authService, journalService, etc.)
 * and include 'export: exportService' in the apiService object at the bottom.
 */

// =========================================
// Export Service
// =========================================

/**
 * Service for exporting user's knowledge base
 * Endpoints: /api/export/*
 */
export const exportService = {
  /**
   * Get export preview (counts and available formats)
   * Useful for showing user what will be exported
   * @returns {Promise<Object>} Preview with counts and format options
   */
  getPreview: async () => {
    try {
      console.log('Export Service: Fetching export preview');
      const response = await axiosInstance.get('/api/export/preview');
      return response.data;
    } catch (error) {
      console.error('Export Service: Preview failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Export knowledge base as JSON
   * Downloads a ZIP file containing JSON files
   * @returns {Promise<void>} Triggers file download
   */
  exportAsJSON: async () => {
    try {
      console.log('Export Service: Starting JSON export');
      
      const response = await axiosInstance.get('/api/export?format=json', {
        responseType: 'blob'  // Important: receive as blob for file download
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'constellation-kg-export.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export Service: JSON export completed');
      return { success: true, filename };
    } catch (error) {
      console.error('Export Service: JSON export failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Export knowledge base as CSV
   * Downloads a ZIP file containing CSV files
   * @returns {Promise<void>} Triggers file download
   */
  exportAsCSV: async () => {
    try {
      console.log('Export Service: Starting CSV export');
      
      const response = await axiosInstance.get('/api/export?format=csv', {
        responseType: 'blob'  // Important: receive as blob for file download
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'constellation-kg-export.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export Service: CSV export completed');
      return { success: true, filename };
    } catch (error) {
      console.error('Export Service: CSV export failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  }
};

// =========================================
// AI Service
// =========================================
export const aiService = {
  /**
   * Get available query types
   * @returns {Promise<Array>} List of query types with descriptions
   */
  getQueryTypes: async () => {
    try {
      const response = await axiosInstance.get('/api/ai/query-types');
      return response.data.queryTypes;
    } catch (error) {
      console.error('Failed to fetch query types:', error);
      throw error;
    }
  },

  // =========================================
  // Progressive Disclosure Methods
  // =========================================

  /**
   * Preview search results (entry counts, token estimate, concept tags).
   * Step 2 of progressive disclosure flow. Since Phase B.1 (October 27, 2026)
   * it carries no prioritization options; `retrievalFailures` flags any
   * retrieval leg that failed rather than returned zero.
   *
   * @param {string} query - Original raw user query (Phase 1: needed server-side
   *   to generate the query embedding for vector-first hybrid retrieval)
   * @param {Array<string>} searchTerms - User-approved search terms
   * @param {string} provider - AI provider (affects token budget)
   * @returns {Promise<Object>} Preview with entry counts and token estimates
   */
  preview: async (query, searchTerms, provider = 'claude') => {
    console.info('AI Service: Requesting preview', {
      query,
      searchTerms,
      provider,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await axiosInstance.post('/api/ai/preview', {
        query,
        searchTerms,
        provider
      });

      console.debug('AI Service: Preview received', {
        totalEntries: response.data.totalEntries,
        estimatedTokens: response.data.estimatedTokens,
        exceedsBudget: response.data.exceedsBudget,
        timestamp: new Date().toISOString()
      });

      return response.data;
    } catch (error) {
      console.error('AI Service: Preview failed', {
        error: error.message,
        status: error.response?.status,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  /**
   * Run AI analysis with user-approved settings
   * Step 3 (final step) of progressive disclosure flow
   * 
   * @param {string} query - Original user query
   * @param {Array<string>} searchTerms - User-approved search terms
   * @param {Object} options - Analysis options
   * @param {string} options.provider - AI provider
   * @param {string} options.queryType - Type of query
   * @returns {Promise<Object>} AI response with metadata
   *
   * No `strategy` since Phase B.1 (October 27, 2026): the prioritization choice
   * is gone and the server ignores the field.
   */
  analyze: async (query, searchTerms, options = {}) => {
    const { provider = 'claude', queryType = 'explore', extractedTerms = null } = options;

    console.info('AI Service: Starting analysis', {
      searchTerms,
      provider,
      queryType,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await axiosInstance.post('/api/ai/analyze', {
        query,
        searchTerms,
        provider,
        queryType,
        // Phase A: terms extracted during preview, reused by the analysis.
        ...(Array.isArray(extractedTerms) ? { extractedTerms } : {})
      });

      console.debug('AI Service: Analysis complete', {
        success: response.data.success,
        provider: response.data.provider,
        totalTokens: response.data.usage?.totalTokens,
        entriesAnalyzed: response.data.searchMetadata?.totalAnalyzed,
        timestamp: new Date().toISOString()
      });

      return response.data;
    } catch (error) {
      console.error('AI Service: Analysis failed', {
        error: error.message,
        status: error.response?.status,
        timestamp: new Date().toISOString()
      });

      // Handle specific error cases
      if (error.response?.status === 429) {
        const rateLimitError = new Error('Rate limit exceeded. Please try again later.');
        rateLimitError.status = 429;
        rateLimitError.usage = error.response.data?.queryUsage || error.response.data?.usage;
        throw rateLimitError;
      } else if (error.response?.status === 503) {
        throw new Error('AI service is temporarily unavailable. Please try again later.');
      }

      throw error;
    }
  },

  /**
   * Get available prioritization strategies
   *
   * DEAD as of October 27, 2026 (Phase B.1 ruling 5): nothing calls this; the
   * prioritization choice was removed. Kept with its backend route for the
   * one-release `strategy` grace period; remove with it.
   * Utility method for UI
   * 
   * @returns {Promise<Array>} List of strategies with descriptions
   */
  getPrioritizationStrategies: async () => {
    try {
      const response = await axiosInstance.get('/api/ai/prioritization-strategies');
      return response.data.strategies;
    } catch (error) {
      console.error('Failed to fetch prioritization strategies:', error);
      throw error;
    }
  },

  /**
   * Get current user's AI query usage for the week.
   * @returns {Promise<Object>} { used, limit, resetsAt, allowed }
   */
  getUsage: async () => {
    try {
      const response = await axiosInstance.get('/api/ai/usage');
      return response.data.usage;
    } catch (error) {
      console.error('AI Service: Failed to fetch usage:', error);
      return null;
    }
  }
};

// =========================================
// Phil Service (Philosophy Explorer)
// =========================================

export const philService = {
  /**
   * Phil 2.0: Start a new Phil session using AI Explorer syntheses
   * This replaces the old tag-cluster warm-up system
   * @returns {Promise<Object>} Session data with opening or needsExploration flag
   */
  startSession: async () => {
    try {
      console.log('Phil Service: Starting Phil 2.0 session');
      const response = await axiosInstance.post('/api/phil/start-session');

      console.log('Phil Service: Start session response', {
        success: response.data.success,
        needsExploration: response.data.needsExploration,
        hasOpening: !!response.data.opening
      });

      return response.data;
    } catch (error) {
      console.error('Phil Service: Start session failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Initialize a new Phil session (OLD - tag-cluster based)
   * @deprecated Use startSession() for Phil 2.0
   * @returns {Promise<Object>} Session data with opening message and cluster info
   */
  initializeSession: async () => {
    try {
      console.log('Phil Service: Initializing session');
      const response = await axiosInstance.post('/api/phil/session');

      console.log('Phil Service: Session initialized', {
        sessionId: response.data.sessionId,
        hasOpening: !!response.data.opening
      });

      return response.data;
    } catch (error) {
      console.error('Phil Service: Session initialization failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Get session state
   * @param {string} sessionId - Phil session ID
   * @returns {Promise<Object>} Current session state
   */
  getSession: async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/api/phil/session/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Phil Service: Get session failed', {
        error: error.message,
        sessionId
      });
      throw error;
    }
  },

  /**
   * Send a message to Phil
   * @param {string} sessionId - Phil session ID
   * @param {string} message - User's message
   * @returns {Promise<Object>} Phil's response
   */
  sendMessage: async (sessionId, message) => {
    try {
      console.log('Phil Service: Sending message', { sessionId });
      
      const response = await axiosInstance.post(
        `/api/phil/session/${sessionId}/message`,
        { message }
      );
      
      console.log('Phil Service: Response received', {
        responseType: response.data.responseType,
        crisis: response.data.crisis || false
      });
      
      return response.data;
    } catch (error) {
      console.error('Phil Service: Send message failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * End a Phil session
   * @param {string} sessionId - Phil session ID
   * @param {Object} options - Options for ending session
   * @param {boolean} options.persist - Whether to save the conversation (default: true)
   * @param {boolean} options.unconfirmed - Whether this was an auto-save on tab close
   * @returns {Promise<Object>} Confirmation
   */
  endSession: async (sessionId, options = {}) => {
    try {
      const { persist = true, unconfirmed = false } = options;
      const response = await axiosInstance.delete(`/api/phil/session/${sessionId}`, {
        data: { persist, unconfirmed }
      });
      return response.data;
    } catch (error) {
      console.error('Phil Service: End session failed', {
        error: error.message,
        sessionId
      });
      throw error;
    }
  },

  /**
   * Get Phil-ready clusters for the current user
   * This is used by the sidebar to show available topics
   * @param {Object} options - Query options
   * @param {number} options.limit - Max clusters to return (default: 20)
   * @returns {Promise<Object>} Phil-ready clusters with readiness info
   */
  getReadyClusters: async (options = {}) => {
    try {
      const { limit = 20 } = options;
      
      console.log('Phil Service: Fetching ready clusters', { limit });
      
      // This endpoint should return clusters that meet Phil readiness criteria
      // Based on testClusterComputation.js, these are clusters with isPhilReady = true
      const response = await axiosInstance.get('/api/phil/clusters/ready', {
        params: { limit }
      });
      
      console.log('Phil Service: Ready clusters fetched', {
        count: response.data.clusters?.length,
        totalReady: response.data.totalReady
      });
      
      return response.data;
    } catch (error) {
      console.error('Phil Service: Get ready clusters failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Get conversation history
   * @param {Object} options - Query options
   * @param {number} options.limit - Max sessions to return
   * @param {string} options.before - ISO date for pagination
   * @returns {Promise<Object>} Past Phil sessions
   */
  getHistory: async (options = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.set('limit', options.limit.toString());
      if (options.before) queryParams.set('before', options.before);
      
      const response = await axiosInstance.get(`/api/phil/history?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Phil Service: Get history failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Get Phil user statistics
   * @returns {Promise<Object>} Aggregate stats about Phil conversations
   */
  getStats: async () => {
    try {
      const response = await axiosInstance.get('/api/phil/stats');
      return response.data;
    } catch (error) {
      console.error('Phil Service: Get stats failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Get crisis resources for a country
   * @param {string} countryCode - ISO country code (e.g., 'US', 'UK')
   * @returns {Promise<Object>} Crisis hotline information
   */
  getCrisisResources: async (countryCode = 'US') => {
    try {
      const response = await axiosInstance.get('/api/phil/crisis-resources', {
        params: { country: countryCode }
      });
      return response.data;
    } catch (error) {
      console.error('Phil Service: Get crisis resources failed', {
        error: error.message
      });
      throw error;
    }
  },

  /**
   * Complete Phil orientation
   * Marks orientation as complete on user profile
   * @returns {Promise<Object>} Completion timestamp
   */
  completeOrientation: async () => {
    try {
      console.log('Phil Service: Completing orientation');
      const response = await axiosInstance.post('/api/phil/complete-orientation');
      console.log('Phil Service: Orientation completed', {
        completedAt: response.data.completedAt
      });
      return response.data;
    } catch (error) {
      console.error('Phil Service: Complete orientation failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Resolve document reference ids from Phil's [text](doc:id) tokens into the
   * user's own document title + url. Ids the user doesn't own, or documents with
   * no url, are omitted — callers render those references as plain text.
   *
   * @param {string[]} ids - docReferenceIds harvested from Phil's message text
   * @returns {Promise<Object>} map of id → { title, url }
   */
  resolveDocuments: async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return {};
    try {
      const response = await axiosInstance.post('/api/phil/documents/resolve', { ids });
      return response.data?.documents || {};
    } catch (error) {
      // Non-fatal by design: a failed resolve means references render as plain
      // text, which is the same fallback as an unknown id.
      console.error('Phil Service: Document resolve failed', {
        error: error.message,
        status: error.response?.status
      });
      return {};
    }
  }
};

// =========================================
// Journal Service
// =========================================

/**
 * Service for managing journal entries
 * Endpoints: /api/journal/*
 */
export const journalService = {
  /**
   * Create a new journal entry
   * @param {Object} entryData - Entry data
   * @param {string} entryData.title - Entry title (required)
   * @param {string} entryData.content - Entry content (required)
   * @param {string} entryData.tags - Comma-separated tags (optional)
   * @param {string} entryData.imagePath - Local image path reference (optional)
   * @param {Object} entryData.imageMetadata - Image metadata (optional)
   * @returns {Promise<Object>} Created entry
   */
  create: async (entryData) => {
    try {
      console.log('Journal Service: Creating entry', {
        hasTitle: !!entryData.title,
        hasContent: !!entryData.content,
        hasTags: !!entryData.tags,
        hasImage: !!entryData.imagePath
      });

      const response = await axiosInstance.post('/api/journal', entryData);

      console.log('Journal Service: Entry created', {
        journalEntryId: response.data.entry?.journalEntryId
      });

      return response.data;
    } catch (error) {
      console.error('Journal Service: Create failed', {
        error: error.message,
        status: error.response?.status,
        validationErrors: error.response?.data?.errors
      });
      throw error;
    }
  },

  /**
   * Get all journal entries for the current user
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Entries per page (default: 25)
   * @param {string} options.sortOrder - 'asc' or 'desc' (default: 'desc')
   * @param {string} options.search - Optional search term; filters by title,
   *   thoughts, and tags. Omitted when blank to return the full list.
   * @returns {Promise<Object>} Entries list with pagination
   */
  getAll: async (options = {}) => {
    try {
      const { page = 1, limit = 25, sortOrder = 'desc', search = '' } = options;

      console.log('Journal Service: Fetching entries', { page, limit, sortOrder, search });

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortOrder
      });
      if (search && search.trim()) {
        queryParams.set('q', search.trim());
      }

      const response = await axiosInstance.get(`/api/journal?${queryParams}`);

      console.log('Journal Service: Entries fetched', {
        count: response.data.entries?.length,
        total: response.data.pagination?.total
      });

      return response.data;
    } catch (error) {
      console.error('Journal Service: Fetch all failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Get a single journal entry by ID
   * @param {string} entryId - Journal entry ID
   * @returns {Promise<Object>} Journal entry
   */
  getById: async (entryId) => {
    try {
      console.log('Journal Service: Fetching entry', { entryId });

      const response = await axiosInstance.get(`/api/journal/${entryId}`);

      return response.data;
    } catch (error) {
      console.error('Journal Service: Fetch by ID failed', {
        error: error.message,
        status: error.response?.status,
        entryId
      });
      throw error;
    }
  },

  /**
   * Update an existing journal entry
   * @param {string} entryId - Journal entry ID
   * @param {Object} updateData - Fields to update
   * @param {string} updateData.title - Updated title (optional)
   * @param {string} updateData.content - Updated content (optional)
   * @param {string} updateData.tags - Updated tags (optional)
   * @param {string} updateData.imagePath - Updated image path (optional)
   * @returns {Promise<Object>} Updated entry
   */
  update: async (entryId, updateData) => {
    try {
      console.log('Journal Service: Updating entry', {
        entryId,
        fieldsToUpdate: Object.keys(updateData)
      });

      const response = await axiosInstance.put(`/api/journal/${entryId}`, updateData);

      console.log('Journal Service: Entry updated', {
        journalEntryId: response.data.entry?.journalEntryId
      });

      return response.data;
    } catch (error) {
      console.error('Journal Service: Update failed', {
        error: error.message,
        status: error.response?.status,
        entryId,
        validationErrors: error.response?.data?.errors
      });
      throw error;
    }
  },

  /**
   * Delete a journal entry
   * @param {string} entryId - Journal entry ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (entryId) => {
    try {
      console.log('Journal Service: Deleting entry', { entryId });

      const response = await axiosInstance.delete(`/api/journal/${entryId}`);

      console.log('Journal Service: Entry deleted', { entryId });

      return response.data;
    } catch (error) {
      console.error('Journal Service: Delete failed', {
        error: error.message,
        status: error.response?.status,
        entryId
      });
      throw error;
    }
  },

  /**
   * Search journal entries
   * @param {string} searchTerm - Term to search for
   * @param {Object} options - Search options
   * @param {number} options.limit - Max results (default: 25)
   * @returns {Promise<Object>} Search results
   */
  search: async (searchTerm, options = {}) => {
    try {
      const { limit = 25 } = options;

      console.log('Journal Service: Searching', { searchTerm, limit });

      const queryParams = new URLSearchParams({
        q: searchTerm,
        limit: limit.toString()
      });

      const response = await axiosInstance.get(`/api/journal/search?${queryParams}`);

      console.log('Journal Service: Search complete', {
        resultsCount: response.data.entries?.length
      });

      return response.data;
    } catch (error) {
      console.error('Journal Service: Search failed', {
        error: error.message,
        status: error.response?.status,
        searchTerm
      });
      throw error;
    }
  },

  /**
   * Get journal statistics for the current user
   * @returns {Promise<Object>} Journal statistics
   */
  getStats: async () => {
    try {
      console.log('Journal Service: Fetching stats');

      const response = await axiosInstance.get('/api/journal/stats');

      return response.data;
    } catch (error) {
      console.error('Journal Service: Stats fetch failed', {
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  }
};

// =========================================
// Knowledge Service
// =========================================
export const knowledgeService = {
  getEntries: async (page = 1, filters = {}) => {
    console.debug('Getting entries with params:', {
      page,
      filters,
      sourceFilter: filters.sources
    });

    const queryParams = new URLSearchParams({
      page: page.toString()
    });

    // Handle source filters - can be array or string
    if (filters.sources) {
      const sourceParam = Array.isArray(filters.sources) 
        ? filters.sources.join(',') 
        : filters.sources;
      
      if (sourceParam) {
        queryParams.set('sources', sourceParam);
        console.debug('Added source filter:', {
          raw: filters.sources,
          processed: sourceParam
        });
      }
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (key !== 'sources' && value !== undefined && value !== null && value !== '') {
        queryParams.set(key, value);
      }
    });
      
    try {
      const response = await axiosInstance.get(`/api/knowledge/entries?${queryParams}`);
      console.log('Entries response:', {
        totalEntries: response.data.total,
        currentPage: response.data.page,
        selectedSources: filters.sources
      });
      return response.data;
    } catch (error) {
      console.error('Entries request failed:', {
        endpoint: '/api/knowledge/entries',
        error: error.message,
        params: queryParams.toString(),
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },
    
  getCounts: async () => {
    const response = await axiosInstance.get('/api/knowledge/counts');
    return response.data;
  },
  
  updateThought: async (knowledgeId, thoughtText) => {
    const response = await axiosInstance.put(
      `/api/knowledge/user-knowledge/${knowledgeId}/thought`,
      { uThought: thoughtText }
    );
    await cacheHelpers.invalidate(['entries']);
    return response.data;
  },

  // Save (or decline) the user's personal tags for an entry. Pass the raw
  // comma-separated tag string; '' is the explicit "No" state. Drives the MKE
  // "add tags?" prompt — see UserKnowledge.personalTags three-state semantics.
  savePersonalTags: async (knowledgeId, tagString) => {
    const response = await axiosInstance.put(
      `/api/knowledge/user-knowledge/${knowledgeId}/personal-tags`,
      { tags: tagString }
    );
    await cacheHelpers.invalidate(['entries']);
    return response.data;
  },

  deleteEntry: async (knowledgeId) => {
    console.log('Attempting to delete entry with ID:', knowledgeId);
    try {
      const response = await axiosInstance.delete(`/api/knowledge/user-knowledge/${knowledgeId}`);
      await cacheHelpers.invalidate(['entries']);
      return response.data;
    } catch (error) {
      console.error('Delete request failed:', {
        endpoint: `/api/knowledge/user-knowledge/${knowledgeId}`,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },

  deleteFocus: async (knowledgeId) => {
    const response = await axiosInstance.delete(`/api/knowledge/user-knowledge/${knowledgeId}/focus`);
    await cacheHelpers.invalidate(['entries']);   
    return response.data;
  },

  search: async (query, page = 1) => {
    console.log('Executing search with params:', {
      query,
      page,
      endpoint: '/api/knowledge/search'
    });

    try {
      const queryParams = new URLSearchParams({
        q: query,
        page: page.toString(),
        includeThoughts: 'true'
      });

      const response = await axiosInstance.get(`/api/knowledge/search?${queryParams}`);
      console.log('Search response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Search request failed:', {
        endpoint: '/api/knowledge/search',
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },

  removeStarterEntries: async () => {
    console.log('Attempting to remove starter (seed) entries for current user');
    try {
      const response = await axiosInstance.delete('/api/knowledge/user-knowledge/seed');
      await cacheHelpers.invalidate(['entries']);
      return response.data;
    } catch (error) {
      console.error('Remove starter entries request failed:', {
        endpoint: '/api/knowledge/user-knowledge/seed',
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  }
};

// =========================================
// Unexplored Links Service
// =========================================

export const unexploredLinksService = {
  getLinks: async (page = 1, options = {}) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      ...options
    });
    
    const response = await axiosInstance.get(`/api/unexplored-links?${queryParams}`);
    return response.data;
  },

  deleteLink: async (id) => {
    const response = await axiosInstance.delete(`/api/unexplored-links/${id}`);
    return response.data;
  }
};

// =========================================
// Source Service
// =========================================
     
export const sourceService = {
  lookupSource: async (domain) => {
    const response = await axiosInstance.get(`/api/domain-source/lookup/${domain}`);
    return response.data;
  },
  
  submitCorrection: async ({ domain, suggestedName }) => {
    console.debug('Submitting source correction:', { domain, suggestedName });
    try {
      const response = await axiosInstance.post('/api/domain-source/correction', {
        domain,
        suggestedName
      });
    
      // Invalidate relevant caches after successful correction
      await cacheHelpers.invalidate(['sources-with-counts', 'all-sources']);
    
      console.debug('Source correction submitted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Source correction failed:', {
        error: error.message,
        domain,
        suggestedName
      });
      throw error;   
    }
  },
      
  getAllSources: async () => {
    const cacheKey = 'all-sources';
    const cached = await cacheHelpers.get(cacheKey);
    if (cached) return cached;

    const response = await axiosInstance.get('/api/domain-source');
    await cacheHelpers.set(cacheKey, response.data);
    return response.data;
  },
        
  getSourcesWithCounts: async () => {
    const cacheKey = 'sources-with-counts';
    const cached = await cacheHelpers.get(cacheKey);
    if (cached) return cached;

    const response = await axiosInstance.get('/api/domain-source/counts');
    await cacheHelpers.set(cacheKey, response.data);
    return response.data;
  },

  resolveDomains: async (domains) => {
    if (!domains || domains.length === 0) return [];
    const response = await axiosInstance.post(
      '/api/domain-source/resolve',
      { domains }
    );
    return response.data.sources || [];
  },

  getUnidentifiedSources: async () => {
    // Don't cache unidentified sources as they may change frequently
    const response = await axiosInstance.get('/api/domain-source/unidentified');
    return response.data;
  },

  getUnidentifiedDomains: async () => {
    try {
      const response = await axiosInstance.get('/api/domain-source/unidentified-domains');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch unidentified domains:', error);
      throw error;
    }
  }
};

// =========================================
// Admin Service
// =========================================
export const adminService = {
  // User metrics
  getUserMetrics: async (timeframe = '30d') => {
    const response = await axiosInstance.get(`/api/admin/metrics/users?timeframe=${timeframe}`);
    return response.data;
  },
  
  // Knowledge metrics
  getKnowledgeMetrics: async (timeframe = '30d') => {
    const response = await axiosInstance.get(`/api/admin/metrics/knowledge?timeframe=${timeframe}`);
    return response.data;
  },
  
  // Document metrics
  getDocumentMetrics: async () => {
    const response = await axiosInstance.get('/api/admin/metrics/documents');
    return response.data;
  },
  
  // User activity metrics
  getUserActivityMetrics: async (timeframe = '30d') => {
    const response = await axiosInstance.get(`/api/admin/metrics/user-activity?timeframe=${timeframe}`);
    return response.data;
  },
  
  // AI metrics
  getAIMetrics: async (timeframe = '30d') => {
    const response = await axiosInstance.get(`/api/admin/metrics/ai?timeframe=${timeframe}`);
    return response.data;
  },

  // AI cost metrics (Phase 3.4)
  getAICostMetrics: async (timeframe = '30d') => {
    const response = await axiosInstance.get('/api/admin/metrics/ai-costs', { params: { timeframe } });
    return response.data;
  },

  // Waitlist management
  getWaitlist: async () => {
    const response = await axiosInstance.get('/api/admin/waitlist');
    return response.data;
  },

  // Add to waitlist from admin screen
  addToWaitlist: async (userData) => {
    try {
      const response = await axiosInstance.post('/api/admin/waitlist/add', userData);
      return response.data;
    } catch (error) {
      console.error('Failed to add user to waitlist:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },
  
  // Add to waitlist AND approve immediately (creates user account)
  addAndApproveWaitlist: async (userData) => {
    try {
      const response = await axiosInstance.post('/api/admin/waitlist/add-and-approve', userData);
      return response.data;
    } catch (error) {
      console.error('Failed to add and approve user:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },

  approveWaitlistEntry: async (id) => {
    const response = await axiosInstance.post(`/api/admin/waitlist/${id}/approve`);
    return response.data;
  },
  
  denyWaitlistEntry: async (id, reason = '') => {
    const response = await axiosInstance.post(`/api/admin/waitlist/${id}/deny`, { reason });
    return response.data;
  },

  // Activity summary (Phase 2.6 — hero cards on Overview tab)
  getActivitySummary: async () => {
    const response = await axiosInstance.get('/api/admin/metrics/activity-summary');
    return response.data;
  },

  // AI provider health (October 28, 2026 — one line on the Overview tab).
  // Process-local, in-memory record on the server; content-free.
  getProviderHealth: async () => {
    const response = await axiosInstance.get('/api/admin/metrics/provider-health');
    return response.data;
  },

  // User directory (Users tab — read-only)
  // params: { search?, tier?, activity? } — all optional; no params returns all users
  getUsers: async (params = {}) => {
    const response = await axiosInstance.get('/api/admin/users', { params });
    return response.data;
  },

  // Organization management
  getOrganizations: async () => {
    const response = await axiosInstance.get('/api/admin/organizations');
    return response.data;
  },

  createOrganization: async (orgData) => {
    const response = await axiosInstance.post('/api/admin/organizations', orgData);
    return response.data;
  },

  updateOrganization: async (orgId, orgData) => {
    const response = await axiosInstance.patch(`/api/admin/organizations/${orgId}`, orgData);
    return response.data;
  },

  deleteOrganization: async (orgId) => {
    const response = await axiosInstance.delete(`/api/admin/organizations/${orgId}`);
    return response.data;
  },

  getOrganizationMetrics: async (orgId, timeframe = '30d') => {
    const response = await axiosInstance.get(`/api/admin/organizations/${orgId}/metrics`, {
      params: { timeframe }
    });
    return response.data;
  },

  getOrganizationUsers: async (orgId) => {
    const response = await axiosInstance.get(`/api/admin/organizations/${orgId}/users`);
    return response.data;
  },

  deactivateUser: async (orgId, userId) => {
    const response = await axiosInstance.patch(`/api/admin/organizations/${orgId}/users/${userId}/deactivate`);
    return response.data;
  },

  bulkEnroll: async (file, autoApprove = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('autoApprove', autoApprove.toString());
    const response = await axiosInstance.post('/api/admin/organizations/bulk-enroll', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getOrganizationDistribution: async (orgId, timeframe = '90d') => {
    const response = await axiosInstance.get(`/api/admin/organizations/${orgId}/distribution`, {
      params: { timeframe }
    });
    return response.data;
  }
};

// =========================================
// Collective Intelligence Service
// =========================================

export const ciService = {
  /**
   * Search the collective. The PoC sidebar only uses universal scope.
   */
  search: async (
    query,
    page = 1,
    scopeType = 'universal',
    scopeId = null,
    sourceDomains = null
  ) => {
    const queryParams = new URLSearchParams({
      searchTerm: query,
      scopeType,
      page: page.toString(),
      searchMode: 'keyword'
    });
    if (scopeId) queryParams.set('scopeId', scopeId);
    if (sourceDomains && sourceDomains.length > 0) {
      queryParams.set('sources', sourceDomains.join(','));
    }

    const response = await axiosInstance.get(
      `/api/ci/search?${queryParams}`
    );
    return response.data;
  },

  /**
   * Add an existing CI focus to the user's KB.
   * Returns 201 on success, 409 when already claimed, 404 when missing.
   */
  claim: async (focusEntryId) => {
    const response = await axiosInstance.post('/api/ci/claim', { focusEntryId });
    return response.data;
  },

  claimBulk: async (focusEntryIds) => {
    const response = await axiosInstance.post(
      '/api/ci/claim/bulk',
      { focusEntryIds }
    );
    return response.data;
  },

  /**
   * Universal-scope summary counts for the sidebar stats panel.
   */
  getCounts: async () => {
    const response = await axiosInstance.get('/api/ci/counts');
    return response.data;
  },
};

// =========================================
// Consolidated Service Export
// =========================================
export const apiService = {
  auth: authService,
  knowledge: knowledgeService,
  unexploredLinks: unexploredLinksService,
  source: sourceService,
  admin: adminService,
  cache: cacheHelpers,
  ai: aiService,
  phil: philService,
  waitlist: waitlistService,
  journal: journalService,
  export: exportService,
  ci: ciService
};
